import { Box, HStack, VStack } from "@chakra-ui/react";
import {
  AddCircle as AddCircleIcon,
  DeleteOutline as DeleteOutlineIcon,
  ListOutlined as ListOutlinedIcon,
} from "@mui/icons-material";
import {
  FieldArray,
  FieldArrayRenderProps,
  useField,
  useFormikContext,
} from "formik";
import { FC, useCallback, useMemo, useState } from "react";

import { SymbolOutlineButton } from "~/src/components/common/Button";
import { FormControl } from "~/src/components/common/forms/FormControl";
import { FormikSelectField } from "~/src/components/common/forms/FormikSelectField";
import { IconButton } from "~/src/components/common/IconButton";
import { Switch } from "~/src/components/common/Switch";
import { useOpenState } from "~/src/lib/hooks";

import { findFunnelOptionByImportedId } from "../popupImportFunnelOptions";
import { usePopupImportFunnelOptions } from "../PopupImportFunnelOptionsContext";
import { popupImportSelectWithMenuPortalProps } from "../popupImportSelectMenuPortal";
import {
  type PopupImportSourceOption,
  findSourceOptionByImportedId,
} from "../popupImportSourceOptions";
import { usePopupImportSourceOptions } from "../PopupImportSourceOptionsContext";
import type { PopupImportFormValues } from "../validationSchema";

import { SourceTagReferenceModal } from "./SourceTagReferenceModal";

const selectProps = popupImportSelectWithMenuPortalProps;

type ModalContext = {
  sourceId: string;
  sourceName: string;
  funnelSlug: string;
  funnelName: string;
  landingPageId: string;
};

export type Props = {
  name: string;
  popupIndex: number;
};

type SourceIdListRowProps = {
  name: string;
  index: number;
  canOpenModal: boolean;
  sourceOptions: PopupImportSourceOption[];
  onRemove: (index: number) => void;
  onOpenModal: (index: number) => void;
};

const SourceIdListRow: FC<SourceIdListRowProps> = ({
  name,
  index,
  canOpenModal,
  sourceOptions,
  onRemove,
  onOpenModal,
}) => {
  const handleRemove = useCallback(() => {
    onRemove(index);
  }, [index, onRemove]);
  const handleOpenModal = useCallback(() => {
    onOpenModal(index);
  }, [index, onOpenModal]);

  return (
    <HStack width="100%">
      <Box flex={1}>
        <FormikSelectField
          name={`${name}.${index}`}
          options={sourceOptions}
          {...selectProps}
        />
      </Box>
      <Box>
        <IconButton
          size="24px"
          label="入稿用リンク一覧を表示"
          icon={<ListOutlinedIcon fontSize="small" />}
          isDisabled={!canOpenModal}
          onClick={handleOpenModal}
        />
      </Box>
      <Box>
        <IconButton
          size="24px"
          icon={<DeleteOutlineIcon />}
          onClick={handleRemove}
        />
      </Box>
    </HStack>
  );
};

type AddSourceIdButtonProps = {
  onAdd: (value: string) => void;
};

const AddSourceIdButton: FC<AddSourceIdButtonProps> = ({ onAdd }) => {
  const handleClick = useCallback(() => {
    onAdd("");
  }, [onAdd]);

  return (
    <SymbolOutlineButton icon={<AddCircleIcon />} onClick={handleClick}>
      条件を追加
    </SymbolOutlineButton>
  );
};

export const PopupImportSourceIdsListCell: FC<Props> = ({
  name,
  popupIndex,
}) => {
  const { sourceOptions } = usePopupImportSourceOptions();
  const { funnelOptions } = usePopupImportFunnelOptions();
  const { values } = useFormikContext<PopupImportFormValues>();
  const [resourceIds, , { setValue }] = useField<string[]>(name);
  const [modalContext, setModalContext] = useState<ModalContext | null>(null);

  const {
    isOpen: sectionOpen,
    onOpen,
    onClose,
  } = useOpenState(resourceIds.value ? resourceIds.value.length > 0 : false);

  const rawFunnelId = values.popups[popupIndex]?.verificationFunnelId ?? "";
  const funnel = useMemo(
    () => findFunnelOptionByImportedId(rawFunnelId, funnelOptions),
    [rawFunnelId, funnelOptions]
  );

  const handleSectionToggle = useCallback(() => {
    if (sectionOpen) {
      onClose();
      setValue([]);
    } else {
      onOpen();
    }
  }, [sectionOpen, onClose, onOpen, setValue]);

  const handleCloseModal = useCallback(() => {
    setModalContext(null);
  }, []);

  const openModalForIndex = useCallback(
    (index: number) => {
      if (!funnel?.landingPageId) return;
      const rawSourceId = resourceIds.value?.[index] ?? "";
      const source = findSourceOptionByImportedId(rawSourceId, sourceOptions);
      if (!source) return;
      setModalContext({
        sourceId: source.value,
        sourceName: source.label,
        funnelSlug: funnel.slug,
        funnelName: funnel.name,
        landingPageId: funnel.landingPageId,
      });
    },
    [funnel, resourceIds.value, sourceOptions]
  );

  const renderFieldArray = useCallback(
    (arrayHelpers: FieldArrayRenderProps) => (
      <FormControl label="" hideLabel>
        <Box width="100%" display="block">
          <Switch isChecked={sectionOpen} onChange={handleSectionToggle} />
        </Box>
        <VStack width="100%">
          {sectionOpen && (
            <>
              {resourceIds.value &&
                resourceIds.value.length > 0 &&
                resourceIds.value.map((id, index) => {
                  const source = findSourceOptionByImportedId(
                    id,
                    sourceOptions
                  );
                  const canOpenModal = !!funnel?.landingPageId && !!source;
                  return (
                    <SourceIdListRow
                      key={index}
                      name={name}
                      index={index}
                      canOpenModal={canOpenModal}
                      sourceOptions={sourceOptions}
                      onRemove={arrayHelpers.remove}
                      onOpenModal={openModalForIndex}
                    />
                  );
                })}
              <Box width="100%">
                <AddSourceIdButton onAdd={arrayHelpers.push} />
              </Box>
            </>
          )}
        </VStack>
      </FormControl>
    ),
    [
      funnel,
      handleSectionToggle,
      name,
      openModalForIndex,
      resourceIds.value,
      sectionOpen,
      sourceOptions,
    ]
  );

  return (
    <>
      <Box minW="300px" py="2px">
        <FieldArray name={name} render={renderFieldArray} />
      </Box>
      {modalContext && (
        <SourceTagReferenceModal
          key={`${modalContext.funnelSlug}-${modalContext.sourceId}`}
          isOpen
          onClose={handleCloseModal}
          funnelSlug={modalContext.funnelSlug}
          funnelName={modalContext.funnelName}
          landingPageId={modalContext.landingPageId}
          sourceId={modalContext.sourceId}
          sourceName={modalContext.sourceName}
        />
      )}
    </>
  );
};
